import type { Attachment } from 'svelte/attachments';

interface ChatAutoScrollOptions {
	/** Read reactively so sending a new user message re-enables auto-scroll after the user had scrolled up. */
	getMessages: () => { role: string }[];
	/** Auto-scroll is suspended while the latest assistant message is still streaming, so users can read as tokens land. */
	isLatestInProgress: () => boolean;
	/** Extra reactive dependency that should force a spacer recalculation, e.g. the active conversation id. */
	getExtraDep?: () => unknown;
}

/**
 * Shared "new message rises to the top" auto-scroll behavior for the standard and antonio chat
 * views: a spacer at the end of the message list reserves room so the latest exchange lands near
 * the top of the viewport, kept pinned there via ResizeObserver as the response streams in,
 * unless the user has manually scrolled up.
 *
 * The "latest exchange" is anchored to the actual latest `.message.user` element rather than a
 * fixed count of trailing DOM children, so it stays correct regardless of what precedes it —
 * a long history, or (as in the antonio chat) a seeded assistant welcome message with no prior
 * user message at all.
 */
export class ChatAutoScroll {
	containerEl: HTMLElement | null = $state(null);
	userScrolledManually = $state(false);
	private layoutTrigger = $state(0);
	private autoScrollTimeout: ReturnType<typeof setTimeout> | undefined;

	constructor(private readonly options: ChatAutoScrollOptions) {
		$effect(() => {
			if (this.options.getMessages().at(-1)?.role === 'user') {
				this.userScrolledManually = false;
			}
		});

		// Jump straight to the anchor with no animation when switching to a different
		// conversation (e.g. picking one from the sidebar) — a smooth scroll only makes
		// sense while staying within the same conversation as it grows.
		$effect(() => {
			void this.options.getExtraDep?.();
			this.userScrolledManually = false;
			this.jumpToAnchor();
		});
	}

	/** Clearance reserved above the pinned message, read from the message list's own top padding so it always matches the container's actual visual padding instead of a hardcoded constant. */
	private get headroomPx(): number {
		const messagesContainer = this.containerEl?.firstElementChild as HTMLElement | null;
		if (!messagesContainer) {
			return 0;
		}

		const style = getComputedStyle(messagesContainer);

		const totalHeadroom = parseFloat(style.paddingBottom) || 0;
		const FIXED_ADDITIONAL_HEADROOM = 16; // to not be swallowed by the gradient
		return totalHeadroom + FIXED_ADDITIONAL_HEADROOM;
	}

	/** The latest user message's element, which anchors the reservation — everything after it (the assistant reply, tool call blocks, loading indicator, ...) is "the current exchange" regardless of what precedes it. Falls back to the last message before the spacer when there's no user message yet (e.g. a seeded assistant welcome message with nothing sent yet), so there's always something to pin. */
	private findAnchor(): HTMLElement | null {
		const messagesContainer = this.containerEl?.firstElementChild as HTMLElement | null;
		const children = [...(messagesContainer?.children ?? [])] as HTMLElement[];
		const contentChildren = children.slice(0, -1); // exclude the trailing spacer

		return (
			contentChildren.findLast((el) => el.classList.contains('user')) ??
			contentChildren.at(-1) ??
			null
		);
	}

	readonly spacerHeight = $derived.by(() => {
		void this.layoutTrigger;
		void this.options.getExtraDep?.();

		if (!this.containerEl) {
			return '0px';
		}

		const anchor = this.findAnchor();
		const messagesContainer = this.containerEl.firstElementChild as HTMLElement | null;
		const spacerEl = messagesContainer?.lastElementChild as HTMLElement | null;
		if (!anchor || !spacerEl) {
			return '0px';
		}

		const trailingHeight = spacerEl.offsetTop - anchor.offsetTop;

		return `${Math.max(this.containerEl.clientHeight - trailingHeight - this.headroomPx, 0)}px`;
	});

	/** Anchor's target scrollTop, or null if there's nothing to pin yet. */
	private anchorScrollTop(): number | null {
		if (!this.containerEl) {
			return null;
		}

		const anchor = this.findAnchor();
		if (!anchor) {
			return null;
		}

		return Math.max(anchor.offsetTop - this.headroomPx, 0);
	}

	scrollToBottom() {
		const top = this.anchorScrollTop();
		if (top === null || !this.containerEl) {
			return;
		}

		// Fallback in case `scrollend` doesn't fire (e.g. unsupported browser); the real
		// clear happens on scrollend so this doesn't race the actual animation duration,
		// which varies with scroll distance and can exceed a fixed guess.
		clearTimeout(this.autoScrollTimeout);
		this.autoScrollTimeout = setTimeout(() => {
			this.autoScrollTimeout = undefined;
		}, 2000);

		this.containerEl.scrollTo({ top, behavior: 'smooth' });
	}

	/** Snaps to the anchor immediately, with no animation — used when switching conversations. */
	private jumpToAnchor() {
		const top = this.anchorScrollTop();
		if (top === null || !this.containerEl) {
			return;
		}

		clearTimeout(this.autoScrollTimeout);
		this.autoScrollTimeout = undefined;

		this.containerEl.scrollTo({ top, behavior: 'instant' });
	}

	/** Attach to the scrollable container element (its first child must be the message list). */
	attach(): Attachment {
		return (element) => {
			this.containerEl = element as HTMLElement;

			const scrollHandler = () => {
				if (this.autoScrollTimeout) {
					return;
				}
				const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
				this.userScrolledManually = distanceFromBottom > 100;
			};
			element.addEventListener('scroll', scrollHandler);

			const scrollEndHandler = () => {
				clearTimeout(this.autoScrollTimeout);
				this.autoScrollTimeout = undefined;
			};
			element.addEventListener('scrollend', scrollEndHandler);

			const resizeHandler = () => {
				this.layoutTrigger++;
				if (!this.userScrolledManually) {
					this.scrollToBottom();
				}
			};
			window.visualViewport?.addEventListener('resize', resizeHandler);

			// ResizeObserver (not MutationObserver) because we specifically care about the
			// message list's rendered height changing, regardless of what caused it — a new
			// message, but also a CSS-transition-driven expand/collapse (e.g. ResearchInfoBlock's
			// slide transition, which only touches inline styles frame-by-frame after its initial
			// childList mutation) or an async image load, neither of which a childList/subtree
			// MutationObserver would catch as the size actually settles.
			const layoutObserver = new ResizeObserver(() => {
				this.layoutTrigger++;
				if (!this.options.isLatestInProgress() && !this.userScrolledManually) {
					this.scrollToBottom();
				}
			});
			layoutObserver.observe(element.firstElementChild ?? element);

			return () => {
				element.removeEventListener('scroll', scrollHandler);
				element.removeEventListener('scrollend', scrollEndHandler);
				window.visualViewport?.removeEventListener('resize', resizeHandler);
				layoutObserver.disconnect();
			};
		};
	}
}
